import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Star } from 'lucide-react';
import { format } from 'date-fns';
import { reviewService } from '../../../../services/reviewService';
import AppImage from '../../../../components/AppImage';

const RelatedReviews = ({ productId, currentReviewId }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRelatedReviews();
  }, [productId, currentReviewId]);

  const fetchRelatedReviews = async () => {
    try {
      setLoading(true);
      const data = await reviewService?.getAllReviews(
        { productId },
        4,
        0
      );
      
      // Filter out current review
      const filtered = data?.filter(r => r?.id !== currentReviewId);
      setReviews(filtered?.slice(0, 3));
    } catch (error) {
      console.error('Failed to fetch related reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Other Reviews for this Product
        </h3>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      </div>
    );
  }

  if (reviews?.length === 0) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">
        Other Reviews for this Product
      </h3>
      <div className="space-y-4">
        {reviews?.map((review) => (
          <Link
            key={review?.id}
            to={`/reviews/${review?.id}`}
            className="block p-4 border border-gray-200 rounded-lg hover:border-blue-500 hover:shadow-md transition-all duration-200"
          >
            <div className="flex items-start gap-3 mb-2">
              <AppImage
                src={review?.avatarUrl}
                alt={review?.avatarAlt || `${review?.reviewerName} avatar`}
                className="w-10 h-10 rounded-full object-cover flex-shrink-0"
              />
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-gray-900 text-sm truncate">
                  {review?.reviewerName}
                </h4>
                <div className="flex items-center gap-2 mt-1">
                  <div className="flex items-center">
                    {[1, 2, 3, 4, 5]?.map((star) => (
                      <Star
                        key={star}
                        className={`w-3 h-3 ${
                          star <= review?.rating
                            ? 'fill-yellow-400 text-yellow-400' :'text-gray-300'
                        }`}
                      />
                    ))}
                  </div>
                  <span className="text-xs text-gray-500">
                    {format(new Date(review?.createdAt), 'MMM dd')}
                  </span>
                </div>
              </div>
            </div>
            <p className="text-sm text-gray-700 font-medium mb-1 line-clamp-1">
              {review?.title}
            </p>
            <p className="text-xs text-gray-600 line-clamp-2">
              {review?.content}
            </p>
          </Link>
        ))}
      </div>

      <Link
        to={`/reviews?productId=${productId}`}
        className="block mt-4 text-center text-sm text-blue-600 hover:text-blue-700 font-medium"
      >
        View all reviews for this product →
      </Link>
    </div>
  );
};

export default RelatedReviews;